import { Permission } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import { LinkPermissionMode, type ILayoutSubMenu } from "src/contexts/layouts/LayoutContext";

import {
    toSettingsNavigationLayoutLink,
    type ISettingsNavigationDestination
} from "./settingsNavigation";

/**
 * Builds trial-scoped item destinations with the established labels, cards and permission requirements.
 *
 * Postconditions: A fresh destination list is returned; the numeric trial ID is interpolated without
 * validation. No authorization check or navigation occurs while constructing the list.
 */
export const createTrialItemsDestinations = (trialId: number): ISettingsNavigationDestination[] => {
    const basePath = `/trials/${trialId}/items`;

    return [
        {
            label: "All Items",
            href: `${basePath}/all-items`,
            // Shows every item type, so it needs each type's view permission.
            permissionMode: LinkPermissionMode.All,
            permissions: [
                Permission.TrialItemView,
                Permission.TrialInstitutionItemInstanceView,
                Permission.TrialGroupView,
                Permission.PatientItemView
            ],
            card: {
                description: "View all operational items across the trial and its linked owners.",
                icon: AppSettings.icons.models.item
            }
        },
        {
            label: "Trial Items",
            href: `${basePath}/trial-items`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialItemView],
            card: {
                description: "View and manage items collected directly against this trial.",
                icon: AppSettings.icons.models.trialItem
            }
        },
        {
            label: "Institution Items",
            href: `${basePath}/institution-items`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialInstitutionItemInstanceView],
            card: {
                description:
                    "View institution items across every accessible institution on this trial.",
                icon: AppSettings.icons.models.trialInstitutionItem
            }
        },
        {
            label: "Group Items",
            href: `${basePath}/group-items`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.TrialGroupView],
            card: {
                description: "View Group Items across every Group linked to this trial.",
                icon: AppSettings.icons.models.trialGroupItem
            }
        },
        {
            label: "Participant Items",
            href: `${basePath}/participant-items`,
            permissionMode: LinkPermissionMode.Any,
            permissions: [Permission.PatientItemView],
            card: {
                description: "View participant items across this trial.",
                icon: AppSettings.icons.models.patientItem
            }
        }
    ];
};

/**
 * Collects distinct item-destination permissions in first-seen order; ID zero is used only to build metadata.
 */
export const trialItemsPermissions = [
    ...new Set(createTrialItemsDestinations(0).flatMap(({ permissions }) => permissions))
];

/**
 * Builds the trial-context submenu by projecting the item destinations into layout links.
 */
export const createTrialItemsSubMenu = (trialId: number): ILayoutSubMenu => ({
    code: "trialItems",
    context: { trialId },
    items: createTrialItemsDestinations(trialId).map(toSettingsNavigationLayoutLink)
});

/**
 * Builds the Items section with its shared permission list and fresh trial-specific children.
 */
export const createTrialItemsSection = (trialId: number): ISettingsNavigationDestination => ({
    label: "Items",
    title: "Items",
    href: `/trials/${trialId}/items`,
    description:
        "View items collected against the trial, its institutions, Groups, and participants.",
    permissionMode: LinkPermissionMode.Any,
    permissions: trialItemsPermissions,
    card: {
        description:
            "View items collected against the trial, its institutions, Groups, and participants.",
        icon: AppSettings.icons.models.item
    },
    children: createTrialItemsDestinations(trialId)
});
